import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { usersAPI } from '../services/api';
import {
  ChartBarIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ArrowTrendingUpIcon,
  FunnelIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

function ClickUpAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [departments, setDepartments] = useState([]);
  
  // Performance metrics states
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [performanceDays, setPerformanceDays] = useState(7);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [selectedPerformanceDept, setSelectedPerformanceDept] = useState('all');
  const [selectedPerformanceUser, setSelectedPerformanceUser] = useState('all');
  const [userPerformanceData, setUserPerformanceData] = useState(null);

  useEffect(() => {
    loadAnalytics();
    loadPerformanceSummary();
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await usersAPI.getDepartmentsWithCount();
      const departmentNames = response.data?.departments?.map(dept => dept.name) || [];
      console.log('ClickUp Analytics loaded departments:', departmentNames);
      setDepartments(departmentNames);
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  };

  useEffect(() => {
    loadPerformanceSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performanceDays, selectedPerformanceDept, selectedPerformanceUser]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      console.log('📡 Fetching ClickUp analytics...');
      const response = await axios.get('/api/clickup/analytics', {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('📥 Analytics response:', response.data);

      if (response.data.success) {
        console.log('✅ Analytics loaded successfully');
        console.log('   Total users:', response.data.userTaskDetails?.length);
        console.log('   Overall stats:', response.data.overallStats);
        
        // Log first few users to verify data
        if (response.data.userTaskDetails && response.data.userTaskDetails.length > 0) {
          console.log('   Top 3 users:');
          response.data.userTaskDetails.slice(0, 3).forEach((u, i) => {
            console.log(`   ${i + 1}. ${u.name} (${u.email}): ${u.taskCount} tasks`);
            if (u.tasks && u.tasks.length > 0) {
              console.log(`      First task: ${u.tasks[0].name}`);
            }
          });
        }
        
        setAnalytics(response.data);
      }
    } catch (err) {
      console.error('❌ Error loading ClickUp analytics:', err);
      console.error('   Error details:', err.response?.data || err.message);
      toast.error('Failed to load ClickUp analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadPerformanceSummary = async () => {
    try {
      setPerformanceLoading(true);
      const token = localStorage.getItem('token');
      
      // If individual user is selected, load user performance
      if (selectedPerformanceUser !== 'all') {
        console.log('📊 Loading user performance for:', selectedPerformanceUser);
        const response = await axios.get(`/api/clickup/performance/user/${selectedPerformanceUser}`, {
          params: { days: performanceDays },
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setUserPerformanceData(response.data);
          setPerformanceSummary(null);
        }
      }
      // If department is selected, load department performance
      else if (selectedPerformanceDept !== 'all') {
        console.log('📊 Loading department performance for:', selectedPerformanceDept);
        const response = await axios.get(`/api/clickup/performance/department/${encodeURIComponent(selectedPerformanceDept)}`, {
          params: { days: performanceDays },
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setPerformanceSummary(response.data);
          setUserPerformanceData(null);
        }
      }
      // Otherwise load overall summary
      else {
        console.log('📊 Loading overall performance summary...');
        const response = await axios.get('/api/clickup/performance/summary', {
          params: { days: performanceDays },
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setPerformanceSummary(response.data);
          setUserPerformanceData(null);
        }
      }
    } catch (err) {
      console.error('❌ Error loading performance:', err);
      // Don't show error toast - performance is optional
    } finally {
      setPerformanceLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');
      
      // Clear cache first
      await axios.post('/api/clickup/clear-cache', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reload analytics, performance, and departments
      await Promise.all([
        loadAnalytics(),
        loadPerformanceSummary(),
        loadDepartments()
      ]);
      
      toast.success('Analytics refreshed successfully!');
    } catch (err) {
      toast.error('Failed to refresh analytics');
    } finally {
      setRefreshing(false);
    }
  };

  const filteredUsers = analytics?.userTaskDetails?.filter(user => {
    const matchesSearch = !searchTerm || 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = selectedDepartment === 'all' || 
      user.department === selectedDepartment;

    return matchesSearch && matchesDepartment;
  }) || [];

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('complete') || statusLower.includes('closed')) return 'bg-green-100 text-green-800';
    if (statusLower.includes('progress')) return 'bg-yellow-100 text-yellow-800';
    if (statusLower.includes('review')) return 'bg-purple-100 text-purple-800';
    if (statusLower.includes('todo') || statusLower.includes('open')) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const priorityLower = priority?.toLowerCase() || '';
    if (priorityLower === 'urgent') return 'bg-red-100 text-red-800 border-red-300';
    if (priorityLower === 'high') return 'bg-orange-100 text-orange-800 border-orange-300';
    if (priorityLower === 'normal') return 'bg-blue-100 text-blue-800 border-blue-300';
    if (priorityLower === 'low') return 'bg-gray-100 text-gray-800 border-gray-300';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin h-20 w-20 border-8 border-blue-200 border-t-blue-600 rounded-full mx-auto"></div>
            <ChartBarIcon className="h-10 w-10 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="mt-6 text-gray-600 font-medium text-lg">Loading ClickUp Analytics...</p>
          <p className="text-gray-500 text-sm mt-2">Fetching tasks from all departments</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium text-lg">Failed to load analytics</p>
          <button
            onClick={loadAnalytics}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 overflow-x-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shadow-2xl overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between py-8">
            <div className="flex items-center">
              <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border-2 border-white/40 shadow-2xl ring-4 ring-white/10">
                <ChartBarIcon className="h-10 w-10 text-white" />
              </div>
              <div className="ml-5">
                <h1 className="text-3xl font-bold text-white drop-shadow-lg">
                  ClickUp Analytics
                </h1>
                <p className="text-sm text-white/90 mt-1 font-medium">
                  Comprehensive task tracking across all departments
                </p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="w-full sm:w-auto group inline-flex items-center justify-center px-6 py-3 border-2 border-white/30 text-sm font-semibold rounded-xl text-white bg-white/10 backdrop-blur-md hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/30 transition-all duration-200 shadow-xl sm:hover:scale-105 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-5 w-5 mr-2 ${refreshing ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 overflow-x-hidden">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-5 mb-8">
          {/* Total Tasks */}
          <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-blue-100 hover:border-blue-400 transform hover:-translate-y-1 w-full max-w-full box-border">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <ClipboardDocumentListIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <dt className="text-sm font-medium text-blue-900 mb-1">Total Tasks</dt>
              <dd className="text-4xl font-bold text-blue-700 group-hover:scale-105 transition-transform duration-300">
                {analytics.overallStats.totalTasks}
              </dd>
              <p className="mt-2 text-xs text-blue-600">
                Active tasks across all users
              </p>
            </div>
          </div>

          {/* Total Users */}
          <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-green-100 hover:border-green-400 transform hover:-translate-y-1 w-full max-w-full box-border">
            <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <UserGroupIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <dt className="text-sm font-medium text-green-900 mb-1">Total Users</dt>
              <dd className="text-4xl font-bold text-green-700 group-hover:scale-105 transition-transform duration-300">
                {analytics.overallStats.totalUsers}
              </dd>
              <p className="mt-2 text-xs text-green-600">
                Active users in system
              </p>
            </div>
          </div>

          {/* Users with Tasks */}
          <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-purple-100 hover:border-purple-400 transform hover:-translate-y-1 w-full max-w-full box-border">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <CheckCircleIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <dt className="text-sm font-medium text-purple-900 mb-1">Users with Tasks</dt>
              <dd className="text-4xl font-bold text-purple-700 group-hover:scale-105 transition-transform duration-300">
                {analytics.overallStats.totalUsersWithTasks}
              </dd>
              <p className="mt-2 text-xs text-purple-600">
                {((analytics.overallStats.totalUsersWithTasks / analytics.overallStats.totalUsers) * 100).toFixed(1)}% of all users
              </p>
            </div>
          </div>

          {/* Average Tasks */}
          <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-orange-100 hover:border-orange-400 transform hover:-translate-y-1 w-full max-w-full box-border">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <ArrowTrendingUpIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <dt className="text-sm font-medium text-orange-900 mb-1">Avg Per User</dt>
              <dd className="text-4xl font-bold text-orange-700 group-hover:scale-105 transition-transform duration-300">
                {analytics.overallStats.avgTasksPerUser}
              </dd>
              <p className="mt-2 text-xs text-orange-600">
                Average tasks per user
              </p>
            </div>
          </div>

          {/* Departments */}
          <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-indigo-100 hover:border-indigo-400 transform hover:-translate-y-1 w-full max-w-full box-border">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <ChartBarIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <dt className="text-sm font-medium text-indigo-900 mb-1">Departments</dt>
              <dd className="text-4xl font-bold text-indigo-700 group-hover:scale-105 transition-transform duration-300">
                {analytics.overallStats.clickupEnabledDepartments}
              </dd>
              <p className="mt-2 text-xs text-indigo-600">
                ClickUp enabled
              </p>
            </div>
          </div>
        </div>

        {/* Performance Metrics Section */}
        {(performanceSummary || userPerformanceData) && (
          <div className="mb-8 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border-2 border-purple-200">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <ArrowTrendingUpIcon className="h-8 w-8 text-purple-600 mr-3" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Performance Metrics</h2>
                  <p className="text-sm text-gray-600">Task completion & productivity trends</p>
                </div>
              </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {/* Department Filter */}
                <select
                  value={selectedPerformanceDept}
                  onChange={(e) => {
                    setSelectedPerformanceDept(e.target.value);
                    setSelectedPerformanceUser('all'); // Reset user selection
                  }}
                  className="border-2 border-purple-300 rounded-lg px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="all">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>

                {/* User Filter - Only show when department is selected */}
                {selectedPerformanceDept !== 'all' && (
                  <select
                    value={selectedPerformanceUser}
                    onChange={(e) => setSelectedPerformanceUser(e.target.value)}
                    className="border-2 border-indigo-300 rounded-lg px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-indigo-500 bg-white"
                  >
                    <option value="all">All Users</option>
                    {analytics?.userTaskDetails
                      ?.filter(user => user.department === selectedPerformanceDept)
                      ?.map(user => (
                        <option key={user.email} value={user.userId}>
                          {user.name}
                        </option>
                      ))}
                  </select>
                )}

                {/* Time Period */}
                <select
                  value={performanceDays}
                  onChange={(e) => setPerformanceDays(parseInt(e.target.value))}
                  className="border-2 border-purple-300 rounded-lg px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value={7}>Last 7 Days</option>
                  <option value={14}>Last 14 Days</option>
                  <option value={30}>Last 30 Days</option>
                </select>
              </div>
            </div>

            {performanceLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-12 w-12 border-4 border-purple-200 border-t-purple-600 rounded-full"></div>
              </div>
            ) : userPerformanceData ? (
              <>
                {/* Individual User Performance */}
                <div className="bg-white rounded-xl p-6 shadow-md border-2 border-indigo-200 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {userPerformanceData.user?.firstName} {userPerformanceData.user?.lastName}
                      </h3>
                      <p className="text-sm text-gray-600">{userPerformanceData.user?.officialEmail}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border-2 border-green-200">
                      <div className="text-xs font-medium text-green-900">Tasks Completed</div>
                      <div className="text-3xl font-bold text-green-700 mt-1">{userPerformanceData.summary.totalCompleted}</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border-2 border-blue-200">
                      <div className="text-xs font-medium text-blue-900">New Tasks</div>
                      <div className="text-3xl font-bold text-blue-700 mt-1">{userPerformanceData.summary.totalNewTasks}</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border-2 border-purple-200">
                      <div className="text-xs font-medium text-purple-900">Avg Daily Tasks</div>
                      <div className="text-3xl font-bold text-purple-700 mt-1">{userPerformanceData.summary.avgDailyTasks}</div>
                    </div>
                    <div className={`bg-gradient-to-br rounded-lg p-4 border-2 ${
                      (userPerformanceData.summary.totalNewTasks - userPerformanceData.summary.totalCompleted) > 0 
                        ? 'from-red-50 to-red-100 border-red-200' 
                        : 'from-emerald-50 to-emerald-100 border-emerald-200'
                    }`}>
                      <div className="text-xs font-medium text-gray-900">Net Change</div>
                      <div className={`text-3xl font-bold mt-1 ${
                        (userPerformanceData.summary.totalNewTasks - userPerformanceData.summary.totalCompleted) > 0 
                          ? 'text-red-700' : 'text-emerald-700'
                      }`}>
                        {(userPerformanceData.summary.totalNewTasks - userPerformanceData.summary.totalCompleted) > 0 ? '+' : ''}
                        {userPerformanceData.summary.totalNewTasks - userPerformanceData.summary.totalCompleted}
                      </div>
                    </div>
                  </div>

                  {/* Daily snapshots */}
                  {userPerformanceData.snapshots && userPerformanceData.snapshots.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 mb-3">Daily Activity</h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {userPerformanceData.snapshots.map((snap, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <div className="font-medium text-gray-900 w-28">{moment(snap.date).format('MMM DD')}</div>
                            <div className="flex items-center gap-4 flex-1">
                              <div><span className="text-xs text-gray-600">Tasks:</span> <span className="font-semibold">{snap.totalTasks}</span></div>
                              {snap.completed > 0 && (
                                <div className="flex items-center">
                                  <CheckCircleIcon className="h-4 w-4 text-green-600 mr-1" />
                                  <span className="text-sm font-semibold text-green-700">{snap.completed}</span>
                                </div>
                              )}
                              {snap.newTasks > 0 && (
                                <div className="flex items-center">
                                  <svg className="h-4 w-4 text-blue-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                  <span className="text-sm font-semibold text-blue-700">{snap.newTasks}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : performanceSummary ? (
              <>
                {/* Department/Overall Performance Summary Cards */}
                <div className="mb-4">
                  {selectedPerformanceDept !== 'all' && (
                    <div className="bg-indigo-100 border-2 border-indigo-300 rounded-lg p-3 mb-4">
                      <p className="text-sm font-semibold text-indigo-900">
                        📊 Showing performance for: <span className="text-indigo-700">{selectedPerformanceDept}</span> Department
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-5 shadow-md border-2 border-green-200">
                    <div className="flex items-center mb-2">
                      <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                      <div className="text-xs font-medium text-green-900">Tasks Completed</div>
                    </div>
                    <div className="text-3xl font-bold text-green-700">{performanceSummary.summary.totalCompleted}</div>
                    <div className="text-xs text-green-600 mt-1">Last {performanceDays} days</div>
                  </div>

                  <div className="bg-white rounded-xl p-5 shadow-md border-2 border-blue-200">
                    <div className="flex items-center mb-2">
                      <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600 mr-2" />
                      <div className="text-xs font-medium text-blue-900">New Tasks</div>
                    </div>
                    <div className="text-3xl font-bold text-blue-700">{performanceSummary.summary.totalNewTasks}</div>
                    <div className="text-xs text-blue-600 mt-1">Last {performanceDays} days</div>
                  </div>

                  <div className="bg-white rounded-xl p-5 shadow-md border-2 border-purple-200">
                    <div className="flex items-center mb-2">
                      <UserGroupIcon className="h-6 w-6 text-purple-600 mr-2" />
                      <div className="text-xs font-medium text-purple-900">
                        {selectedPerformanceDept !== 'all' ? 'Team Members' : 'Active Users'}
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-purple-700">
                      {performanceSummary.summary.totalUsers || performanceSummary.summary.activeUsers}
                    </div>
                    <div className="text-xs text-purple-600 mt-1">
                      {selectedPerformanceDept !== 'all' ? 'In department' : 'With ClickUp tasks'}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-5 shadow-md border-2 border-orange-200">
                    <div className="flex items-center mb-2">
                      <ChartBarIcon className="h-6 w-6 text-orange-600 mr-2" />
                      <div className="text-xs font-medium text-orange-900">Avg Completion</div>
                    </div>
                    <div className="text-3xl font-bold text-orange-700">
                      {performanceSummary.summary.avgCompletionRate || performanceSummary.summary.avgCompletionPerDay}
                    </div>
                    <div className="text-xs text-orange-600 mt-1">
                      {selectedPerformanceDept !== 'all' ? 'Per user' : 'Per day'}
                    </div>
                  </div>
                </div>

                {/* Top Performers or User Rankings */}
                {((performanceSummary.topPerformers && performanceSummary.topPerformers.length > 0) || 
                  (performanceSummary.users && performanceSummary.users.length > 0)) && (
                  <div className="bg-white rounded-xl p-6 shadow-md border-2 border-indigo-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <svg className="h-5 w-5 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      {selectedPerformanceDept !== 'all' ? `${selectedPerformanceDept} Rankings` : 'Top Performers'} (Last {performanceDays} Days)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {(performanceSummary.topPerformers || performanceSummary.users || []).slice(0, 6).map((performer, idx) => (
                        <div 
                          key={performer.userId}
                          className={`rounded-lg p-4 border-2 ${
                            idx === 0 ? 'bg-yellow-50 border-yellow-300' :
                            idx === 1 ? 'bg-gray-50 border-gray-300' :
                            idx === 2 ? 'bg-orange-50 border-orange-300' :
                            'bg-blue-50 border-blue-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-sm ${
                              idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                              idx === 1 ? 'bg-gray-400 text-gray-900' :
                              idx === 2 ? 'bg-orange-400 text-orange-900' :
                              'bg-blue-400 text-blue-900'
                            }`}>
                              {idx + 1}
                            </span>
                            <span className="text-2xl font-bold text-green-700">
                              {performer.completed || performer.totalCompleted}
                            </span>
                          </div>
                          <div className="text-sm font-semibold text-gray-900 truncate">{performer.name}</div>
                          <div className="text-xs text-gray-600 truncate">{performer.email}</div>
                          {performer.department && (
                            <div className="text-xs text-gray-500 mt-1">{performer.department}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Daily Trend Chart */}
                {performanceSummary.dailyTrend && performanceSummary.dailyTrend.length > 0 && (
                  <div className="bg-white rounded-xl p-6 shadow-md border-2 border-gray-200 mt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Daily Trend</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {performanceSummary.dailyTrend.map((day, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="font-medium text-gray-900 w-28">
                            {moment(day.date).format('MMM DD')}
                          </div>
                          <div className="flex items-center gap-4 flex-1">
                            <div className="flex items-center">
                              <span className="text-xs text-gray-600 mr-1">Tasks:</span>
                              <span className="text-sm font-semibold text-gray-900">{day.totalTasks}</span>
                            </div>
                            {day.completed > 0 && (
                              <div className="flex items-center">
                                <CheckCircleIcon className="h-4 w-4 text-green-600 mr-1" />
                                <span className="text-sm font-semibold text-green-700">{day.completed}</span>
                              </div>
                            )}
                            {day.newTasks > 0 && (
                              <div className="flex items-center">
                                <svg className="h-4 w-4 text-blue-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                <span className="text-sm font-semibold text-blue-700">{day.newTasks}</span>
                              </div>
                            )}
                            <div className="text-xs text-gray-600">
                              ({day.userCount} users)
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <ChartBarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">No performance data available yet</p>
                <p className="text-gray-500 text-sm mt-2">
                  Performance tracking starts after the first daily snapshot (runs at 11:59 PM IST)
                </p>
              </div>
            )}
          </div>
        )}

        {/* Department Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
          {analytics.departmentStats.map((dept, idx) => (
            <div 
              key={dept.name}
              className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 hover:border-indigo-300 transition-all duration-300 overflow-hidden transform hover:-translate-y-1 w-full max-w-full box-border"
              style={{
                animation: `fadeInUp 0.5s ease-out ${idx * 0.1}s both`
              }}
            >
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
                <h3 className="text-xl font-bold text-white">{dept.name}</h3>
                <p className="text-sm text-indigo-100">Team ID: {dept.teamId}</p>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="text-xs font-medium text-blue-900">Total Tasks</div>
                    <div className="text-2xl font-bold text-blue-700 mt-1">{dept.totalTasks}</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <div className="text-xs font-medium text-green-900">Active Users</div>
                    <div className="text-2xl font-bold text-green-700 mt-1">
                      {dept.usersWithTasks}/{dept.totalUsers}
                    </div>
                  </div>
                </div>

                {/* Task Status Breakdown */}
                {Object.keys(dept.tasksByStatus).length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Tasks by Status</h4>
                    <div className="space-y-2">
                      {Object.entries(dept.tasksByStatus).map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between">
                          <div className="flex items-center flex-1">
                            <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${getStatusColor(status)} mr-2`}>
                              {status}
                            </span>
                            <div className="flex-1 bg-gray-200 rounded-full h-2 mx-2">
                              <div
                                className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${(count / dept.totalTasks) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                          <span className="text-sm font-bold text-gray-700">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Priority Breakdown */}
                {Object.keys(dept.tasksByPriority).length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Tasks by Priority</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(dept.tasksByPriority).map(([priority, count]) => (
                        <span key={priority} className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${getPriorityColor(priority)}`}>
                          {priority}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                  className="block w-full pl-10 pr-3 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                />
              </div>
            </div>

            {/* Department Filter */}
            <div className="sm:w-64">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FunnelIcon className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                >
                  <option value="all">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="flex items-center px-4 py-3 bg-indigo-50 rounded-xl border-2 border-indigo-200">
              <span className="text-sm font-semibold text-indigo-900">
                {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
              </span>
            </div>
          </div>
        </div>

        {/* User Task List */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">User Task Details</h3>
            <p className="text-sm text-gray-600 mt-1">Click on any user to view their tasks</p>
          </div>

          {/* Mobile Cards */}
          <div className="sm:hidden p-4 space-y-3">
            {filteredUsers.map((user, idx) => (
              <div key={user.userId} className="border-2 border-gray-200 rounded-xl p-4 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                      {user.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.taskCount > 10 ? 'bg-red-100 text-red-800' :
                    user.taskCount > 5 ? 'bg-orange-100 text-orange-800' :
                    user.taskCount > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                  }`}>{user.taskCount}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div className="text-gray-500">Department</div>
                  <div className="text-right text-gray-900">{user.department}</div>
                  <div className="text-gray-500">ClickUp</div>
                  <div className="text-right">{user.clickupEnabled ? (user.error ? 'Not in ClickUp' : 'Connected') : 'Disabled'}</div>
                </div>
                {user.taskCount > 0 && (
                  <button onClick={() => { setSelectedUser(user); setShowTaskModal(true); }} className="mt-3 w-full inline-flex justify-center text-sm font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg py-2">
                    View Tasks →
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Tasks
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                    ClickUp Status
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mb-3" />
                        <p className="text-gray-500 font-medium">No users found</p>
                        <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, idx) => (
                    <tr 
                      key={user.userId}
                      className="hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 transition-all duration-200 cursor-pointer"
                      onClick={() => {
                        if (user.taskCount > 0) {
                          console.log('👆 User clicked:', user.name, '(', user.email, ')');
                          console.log('   Task count:', user.taskCount);
                          console.log('   Tasks:', user.tasks?.length || 0);
                          if (user.tasks && user.tasks.length > 0) {
                            console.log('   First task:', user.tasks[0].name);
                          }
                          setSelectedUser(user);
                          setShowTaskModal(true);
                        }
                      }}
                      style={{
                        animation: `fadeIn 0.3s ease-out ${idx * 0.03}s both`
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center shadow-md ring-2 ring-white">
                            <span className="text-sm font-bold text-white">
                              {user.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{user.department}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold ${
                          user.taskCount > 10 ? 'bg-red-100 text-red-800' :
                          user.taskCount > 5 ? 'bg-orange-100 text-orange-800' :
                          user.taskCount > 0 ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {user.taskCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {user.clickupEnabled ? (
                          user.error ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                              Not in ClickUp
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Connected
                            </span>
                          )
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Disabled
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {user.taskCount > 0 ? (
                          <button className="text-indigo-600 hover:text-indigo-900 font-medium text-sm hover:underline">
                            View Tasks →
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">No tasks</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Task Details Modal */}
      {showTaskModal && selectedUser && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-900 bg-opacity-75 backdrop-blur-sm transition-opacity animate-fadeIn" 
              onClick={() => setShowTaskModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full animate-slideUp">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-14 w-14 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center border-2 border-white/40 shadow-xl">
                      <span className="text-xl font-bold text-white">
                        {selectedUser.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </span>
                    </div>
                    <div className="ml-5">
                      <h3 className="text-2xl font-bold text-white">{selectedUser.name}</h3>
                      <p className="text-sm text-purple-100 mt-1">{selectedUser.email}</p>
                      <p className="text-xs text-white/80 mt-1">
                        {selectedUser.department} • {selectedUser.taskCount} active {selectedUser.taskCount === 1 ? 'task' : 'tasks'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowTaskModal(false)}
                    className="text-white hover:text-gray-200 transition-colors hover:rotate-90 transform duration-300"
                  >
                    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="bg-white px-8 py-6 max-h-[70vh] overflow-y-auto">
                {selectedUser.tasks.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Tasks</h3>
                    <p className="text-gray-500">This user has no active tasks in ClickUp</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedUser.tasks.map((task, idx) => (
                      <div 
                        key={task.id}
                        className="group border-2 border-gray-200 rounded-xl p-5 hover:border-indigo-400 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-gray-50"
                        style={{
                          animation: `fadeInUp 0.3s ease-out ${idx * 0.05}s both`
                        }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-md">
                                {idx + 1}
                              </div>
                              <div className="flex-1">
                                <h4 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-indigo-700 transition-colors">
                                  {task.name}
                                </h4>
                                <div className="flex flex-wrap gap-2 mb-3">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                                    📋 {task.list}
                                  </span>
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(task.status)}`}>
                                    ⚡ {task.status}
                                  </span>
                                  {task.priority && task.priority !== 'None' && (
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(task.priority)}`}>
                                      🎯 {task.priority}
                                    </span>
                                  )}
                                </div>
                                {task.dueDate && (
                                  <div className="flex items-center text-sm text-gray-600">
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    Due: {moment(parseInt(task.dueDate)).format('MMM DD, YYYY')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {task.url && (
                          <a
                            href={task.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors group-hover:underline"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Open in ClickUp
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-5 border-t border-gray-200">
                <button
                  onClick={() => setShowTaskModal(false)}
                  className="w-full sm:w-auto inline-flex justify-center rounded-xl border-2 border-gray-300 shadow-sm px-8 py-3 bg-white text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-gray-200 transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}

export default ClickUpAnalytics;

